import type { BlogArticle } from '../../types/blog'
import { BLOG_DATE_MODIFIED, BLOG_DATE_PUBLISHED } from './blogCommon'

export const implantsCostArticle: BlogArticle = {
  slug: 'dental-implants-cost-london',
  theme: 'teal',
  datePublished: BLOG_DATE_PUBLISHED,
  dateModified: BLOG_DATE_MODIFIED,
  category: { en: 'Implants', ua: 'Імплантація', ru: 'Имплантация', ro: 'Implanturi' },
  readTime: { en: '9 min read', ua: '9 хв читання', ru: '9 мин чтения', ro: '9 min citire' },
  seoTitle: {
    en: 'How Much Do Dental Implants Cost in London? 2026 Guide | Dentico Clinic',
    ua: 'Скільки коштують зубні імпланти в Лондоні? Гід 2026 | Dentico Clinic',
    ru: 'Сколько стоят зубные импланты в Лондоне? Гид 2026 | Dentico Clinic',
    ro: 'Cât costă implanturile dentare în Londra? Ghid 2026 | Dentico Clinic',
  },
  seoDescription: {
    en: 'A clear 2026 guide to dental implant costs in London, what affects the price, what is usually included and how to avoid hidden fees.',
    ua: 'Зрозумілий гід 2026 року про вартість зубних імплантів у Лондоні: що впливає на ціну, що зазвичай входить у вартість і як уникнути прихованих платежів.',
    ru: 'Понятный гид 2026 года о стоимости зубных имплантов в Лондоне: что влияет на цену, что обычно входит в стоимость и как избежать скрытых платежей.',
    ro: 'Un ghid clar pentru 2026 despre costul implanturilor dentare în Londra: ce influențează prețul, ce este inclus de obicei și cum evitați costurile ascunse.',
  },
  h1: {
    en: 'How much do dental implants cost in London? 2026 guide',
    ua: 'Скільки коштують зубні імпланти в Лондоні? Гід 2026',
    ru: 'Сколько стоят зубные импланты в Лондоне? Гид 2026',
    ro: 'Cât costă implanturile dentare în Londra? Ghid 2026',
  },
  cardTitle: {
    en: 'How much do dental implants cost in London? 2026 guide',
    ua: 'Скільки коштують імпланти в Лондоні? Гід 2026',
    ru: 'Сколько стоят импланты в Лондоне? Гид 2026',
    ro: 'Cât costă implanturile dentare în Londra? Ghid 2026',
  },
  cardExcerpt: {
    en: 'What affects the price of a dental implant in London, what a full treatment usually includes, and the questions to ask so there are no hidden fees.',
    ua: 'Від чого залежить ціна імпланта в Лондоні, що зазвичай входить у повне лікування і які питання поставити, щоб уникнути прихованих платежів.',
    ru: 'От чего зависит цена импланта в Лондоне, что обычно входит в полное лечение и какие вопросы задать, чтобы избежать скрытых платежей.',
    ro: 'Ce influențează prețul unui implant în Londra, ce include de obicei tratamentul complet și ce întrebări să puneți pentru a evita costurile ascunse.',
  },
  intro: {
    en: 'A missing tooth affects chewing, speech and the health of the neighbouring teeth, not just your smile. Dental implants are one of the most reliable ways to replace a tooth, but prices in London vary widely and quotes are often hard to compare. This 2026 guide explains what an implant treatment usually includes, typical price ranges, what changes the final cost and how to make sure your quote covers everything.',
    ua: 'Відсутній зуб впливає на жування, мовлення і здоров’я сусідніх зубів, а не лише на усмішку. Зубні імпланти — один із найнадійніших способів замінити зуб, але ціни в Лондоні дуже різняться, а пропозиції клінік складно порівнювати. У цьому гіді 2026 року пояснюємо, що зазвичай входить у лікування, які орієнтовні ціни, що змінює остаточну вартість і як переконатися, що кошторис охоплює все.',
    ru: 'Отсутствующий зуб влияет на жевание, речь и здоровье соседних зубов, а не только на улыбку. Зубные импланты — один из самых надёжных способов заменить зуб, но цены в Лондоне сильно различаются, а предложения клиник сложно сравнивать. В этом гиде 2026 года объясняем, что обычно входит в лечение, какие ориентировочные цены, что меняет итоговую стоимость и как убедиться, что смета покрывает всё.',
    ro: 'Un dinte lipsă afectează masticația, vorbirea și sănătatea dinților vecini, nu doar zâmbetul. Implanturile dentare sunt una dintre cele mai fiabile metode de înlocuire a unui dinte, dar prețurile din Londra variază mult, iar ofertele sunt greu de comparat. Acest ghid pentru 2026 explică ce include de obicei tratamentul, intervalele de preț orientative, ce modifică costul final și cum vă asigurați că oferta acoperă totul.',
  },
  sections: [
    {
      heading: {
        en: 'What a single dental implant treatment usually includes',
        ua: 'Що зазвичай входить у лікування одним імплантом',
        ru: 'Что обычно входит в лечение одним имплантом',
        ro: 'Ce include de obicei tratamentul cu un singur implant',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'An implant is not one procedure but a short treatment journey. Knowing the stages helps you compare quotes fairly:',
            ua: 'Імплантація — це не одна процедура, а кілька етапів лікування. Знання етапів допомагає чесно порівнювати кошториси:',
            ru: 'Имплантация — это не одна процедура, а несколько этапов лечения. Понимание этапов помогает честно сравнивать сметы:',
            ro: 'Un implant nu este o singură procedură, ci un parcurs de tratament. Cunoașterea etapelor vă ajută să comparați corect ofertele:',
          },
        },
        {
          type: 'steps',
          items: [
            {
              title: {
                en: 'Consultation and 3D imaging',
                ua: 'Консультація та 3D-діагностика',
                ru: 'Консультация и 3D-диагностика',
                ro: 'Consultație și imagistică 3D',
              },
              text: {
                en: 'The dentist checks your gums, bite and bone. A CT (CBCT) scan is usually needed to measure bone volume and plan the implant position safely.',
                ua: 'Лікар оцінює ясна, прикус і кістку. Зазвичай потрібен КТ-знімок (CBCT), щоб виміряти об’єм кістки і безпечно спланувати положення імпланта.',
                ru: 'Врач оценивает дёсны, прикус и кость. Обычно нужен КТ-снимок (CBCT), чтобы измерить объём кости и безопасно спланировать положение импланта.',
                ro: 'Medicul verifică gingiile, ocluzia și osul. De obicei este necesară o scanare CT (CBCT) pentru a măsura volumul osos și a planifica sigur poziția implantului.',
              },
            },
            {
              title: {
                en: 'Implant placement',
                ua: 'Встановлення імпланта',
                ru: 'Установка импланта',
                ro: 'Inserarea implantului',
              },
              text: {
                en: 'A titanium implant is placed in the jawbone under local anaesthetic. Most patients describe the procedure as more comfortable than they expected.',
                ua: 'Титановий імплант встановлюють у кістку під місцевою анестезією. Більшість пацієнтів кажуть, що процедура була комфортнішою, ніж вони очікували.',
                ru: 'Титановый имплант устанавливают в кость под местной анестезией. Большинство пациентов говорят, что процедура оказалась комфортнее, чем они ожидали.',
                ro: 'Implantul din titan este inserat în os sub anestezie locală. Majoritatea pacienților spun că procedura a fost mai confortabilă decât se așteptau.',
              },
            },
            {
              title: {
                en: 'Healing (osseointegration)',
                ua: 'Приживлення (остеоінтеграція)',
                ru: 'Приживление (остеоинтеграция)',
                ro: 'Vindecare (osteointegrare)',
              },
              text: {
                en: 'The bone bonds with the implant over roughly 3–6 months. A temporary tooth can often be provided in the meantime.',
                ua: 'Кістка зростається з імплантом приблизно за 3–6 місяців. На цей час часто можна встановити тимчасовий зуб.',
                ru: 'Кость срастается с имплантом примерно за 3–6 месяцев. На это время часто можно установить временный зуб.',
                ro: 'Osul se integrează cu implantul în aproximativ 3–6 luni. Între timp se poate oferi adesea un dinte provizoriu.',
              },
            },
            {
              title: {
                en: 'Abutment and crown',
                ua: 'Абатмент і коронка',
                ru: 'Абатмент и коронка',
                ro: 'Bont și coroană',
              },
              text: {
                en: 'A connector (abutment) and a custom [implant crown](/services/crowns) are fitted, matched to the shade of your natural teeth.',
                ua: 'Встановлюють з’єднувальний елемент (абатмент) та індивідуальну [коронку на імплант](/services/crowns), підібрану під колір ваших зубів.',
                ru: 'Устанавливают соединительный элемент (абатмент) и индивидуальную [коронку на имплант](/services/crowns), подобранную под цвет ваших зубов.',
                ro: 'Se montează bontul (conectorul) și o [coroană pe implant](/services/crowns) personalizată, potrivită nuanței dinților naturali.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'Typical dental implant prices in London in 2026',
        ua: 'Орієнтовні ціни на імпланти в Лондоні у 2026 році',
        ru: 'Ориентировочные цены на импланты в Лондоне в 2026 году',
        ro: 'Prețuri orientative pentru implanturi în Londra în 2026',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'Private implant prices in London commonly range from around £2,000 to £4,000+ per tooth for the complete treatment. The table below shows how that total is usually made up across private clinics:',
            ua: 'Приватні ціни на повне лікування одним імплантом у Лондоні зазвичай становлять приблизно від £2,000 до £4,000+ за зуб. Нижче — як ця сума зазвичай складається в приватних клініках:',
            ru: 'Частные цены на полное лечение одним имплантом в Лондоне обычно составляют примерно от £2,000 до £4,000+ за зуб. Ниже — как эта сумма обычно складывается в частных клиниках:',
            ro: 'Prețurile private pentru tratamentul complet cu un implant în Londra variază de obicei între aproximativ £2,000 și £4,000+ pe dinte. Tabelul arată cum se compune de regulă acest total în clinicile private:',
          },
        },
        {
          type: 'table',
          rows: [
            {
              label: { en: 'Consultation + CT scan', ua: 'Консультація + КТ', ru: 'Консультация + КТ', ro: 'Consultație + CT' },
              value: { en: '£100 – £250', ua: '£100 – £250', ru: '£100 – £250', ro: '£100 – £250' },
            },
            {
              label: { en: 'Implant placement (surgical stage)', ua: 'Встановлення імпланта (хірургічний етап)', ru: 'Установка импланта (хирургический этап)', ro: 'Inserarea implantului (etapa chirurgicală)' },
              value: { en: '£1,000 – £2,000', ua: '£1,000 – £2,000', ru: '£1,000 – £2,000', ro: '£1,000 – £2,000' },
            },
            {
              label: { en: 'Abutment + crown', ua: 'Абатмент + коронка', ru: 'Абатмент + коронка', ro: 'Bont + coroană' },
              value: { en: '£800 – £1,600', ua: '£800 – £1,600', ru: '£800 – £1,600', ro: '£800 – £1,600' },
            },
          ],
          total: {
            label: { en: 'Complete treatment (typical range)', ua: 'Повне лікування (орієнтовно)', ru: 'Полное лечение (ориентировочно)', ro: 'Tratament complet (interval tipic)' },
            value: { en: '£2,000 – £4,000+', ua: '£2,000 – £4,000+', ru: '£2,000 – £4,000+', ro: '£2,000 – £4,000+' },
          },
          note: {
            en: 'These are general London market ranges, not a quote. For Dentico Clinic fees, see our [dental price list](/prices).',
            ua: 'Це загальні ринкові діапазони для Лондона, а не кошторис. Ціни Dentico Clinic дивіться у нашому [прайс-листі](/prices).',
            ru: 'Это общие рыночные диапазоны для Лондона, а не смета. Цены Dentico Clinic смотрите в нашем [прайс-листе](/prices).',
            ro: 'Acestea sunt intervale generale pentru piața din Londra, nu o ofertă. Pentru tarifele Dentico Clinic, consultați [lista noastră de prețuri](/prices).',
          },
        },
      ],
    },
    {
      heading: {
        en: 'What affects the final cost',
        ua: 'Що впливає на остаточну вартість',
        ru: 'Что влияет на итоговую стоимость',
        ro: 'Ce influențează costul final',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              title: { en: 'Bone volume:', ua: 'Об’єм кістки:', ru: 'Объём кости:', ro: 'Volumul osos:' },
              text: {
                en: 'if a tooth has been missing for a long time, bone grafting or a sinus lift may be needed before or during placement.',
                ua: 'якщо зуба немає давно, перед встановленням або під час нього може знадобитися кісткова пластика чи синус-ліфтинг.',
                ru: 'если зуба нет давно, перед установкой или во время неё может понадобиться костная пластика или синус-лифтинг.',
                ro: 'dacă dintele lipsește de mult timp, poate fi necesară adiție osoasă sau sinus lift înainte sau în timpul inserării.',
              },
            },
            {
              title: { en: 'Implant system:', ua: 'Система імплантів:', ru: 'Система имплантов:', ro: 'Sistemul de implant:' },
              text: {
                en: 'well-researched brands usually cost more but offer long-term evidence and easier access to compatible parts in the future.',
                ua: 'добре досліджені бренди зазвичай дорожчі, але мають довгострокові дані та легший доступ до сумісних компонентів у майбутньому.',
                ru: 'хорошо изученные бренды обычно дороже, но имеют долгосрочные данные и более лёгкий доступ к совместимым компонентам в будущем.',
                ro: 'mărcile bine studiate costă de obicei mai mult, dar oferă dovezi pe termen lung și acces mai ușor la componente compatibile în viitor.',
              },
            },
            {
              title: { en: 'Crown material:', ua: 'Матеріал коронки:', ru: 'Материал коронки:', ro: 'Materialul coroanei:' },
              text: {
                en: 'zirconia and porcelain options differ in price and appearance, especially for front teeth.',
                ua: 'цирконій і кераміка різняться ціною та естетикою, особливо для передніх зубів.',
                ru: 'цирконий и керамика различаются ценой и эстетикой, особенно для передних зубов.',
                ro: 'variantele din zirconiu și ceramică diferă ca preț și aspect, mai ales pentru dinții frontali.',
              },
            },
            {
              title: { en: 'Extraction first:', ua: 'Попереднє видалення:', ru: 'Предварительное удаление:', ro: 'Extracție prealabilă:' },
              text: {
                en: 'if the damaged tooth is still present, a [tooth extraction](/services/extraction) is priced separately.',
                ua: 'якщо пошкоджений зуб ще є, [видалення зуба](/services/extraction) оплачується окремо.',
                ru: 'если повреждённый зуб ещё есть, [удаление зуба](/services/extraction) оплачивается отдельно.',
                ro: 'dacă dintele afectat este încă prezent, [extracția dentară](/services/extraction) se taxează separat.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'How to avoid hidden fees: questions to ask',
        ua: 'Як уникнути прихованих платежів: які питання поставити',
        ru: 'Как избежать скрытых платежей: какие вопросы задать',
        ro: 'Cum evitați costurile ascunse: întrebări de pus',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              title: { en: 'Is the crown included?', ua: 'Чи входить коронка?', ru: 'Входит ли коронка?', ro: 'Este inclusă coroana?' },
              text: {
                en: 'Some advertised prices cover only the implant itself. Ask for the full price from consultation to final crown.',
                ua: 'Деякі рекламні ціни охоплюють лише сам імплант. Запитуйте повну вартість — від консультації до коронки.',
                ru: 'Некоторые рекламные цены охватывают только сам имплант. Спрашивайте полную стоимость — от консультации до коронки.',
                ro: 'Unele prețuri promovate acoperă doar implantul. Cereți prețul complet, de la consultație la coroana finală.',
              },
            },
            {
              title: { en: 'Are scans and reviews included?', ua: 'Чи входять знімки та контрольні огляди?', ru: 'Входят ли снимки и контрольные осмотры?', ro: 'Sunt incluse scanările și controalele?' },
              text: {
                en: 'Check whether the CT scan, temporary tooth and follow-up appointments are part of the plan.',
                ua: 'Уточніть, чи входять у план КТ, тимчасовий зуб і контрольні візити.',
                ru: 'Уточните, входят ли в план КТ, временный зуб и контрольные визиты.',
                ro: 'Verificați dacă scanarea CT, dintele provizoriu și controalele fac parte din plan.',
              },
            },
            {
              title: { en: 'Will I get a written plan?', ua: 'Чи буде письмовий план?', ru: 'Будет ли письменный план?', ro: 'Voi primi un plan scris?' },
              text: {
                en: 'A clear written treatment plan with itemised costs lets you decide calmly and compare options.',
                ua: 'Чіткий письмовий план лікування з розписаною вартістю дає змогу спокійно ухвалити рішення і порівняти варіанти.',
                ru: 'Чёткий письменный план лечения с расписанной стоимостью позволяет спокойно принять решение и сравнить варианты.',
                ro: 'Un plan de tratament scris, cu costuri detaliate, vă permite să decideți liniștit și să comparați opțiunile.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'Paying for implants: NHS, finance and alternatives',
        ua: 'Оплата імплантів: NHS, розстрочка та альтернативи',
        ru: 'Оплата имплантов: NHS, рассрочка и альтернативы',
        ro: 'Plata implanturilor: NHS, finanțare și alternative',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'Implants are rarely available on the NHS and are usually limited to specific clinical situations, so most patients in London have them privately. Many private clinics, including ours, offer 0% finance for up to 12 months on selected treatments, subject to approval. If an implant is not suitable for you, your dentist can explain alternatives such as a bridge or a denture.',
            ua: 'Імпланти рідко доступні за NHS і зазвичай лише в окремих клінічних випадках, тому більшість пацієнтів у Лондоні встановлюють їх приватно. Багато приватних клінік, зокрема наша, пропонують 0% розстрочку до 12 місяців на вибрані види лікування за умови схвалення. Якщо імплант вам не підходить, лікар пояснить альтернативи — міст або знімний протез.',
            ru: 'Импланты редко доступны по NHS и обычно только в отдельных клинических случаях, поэтому большинство пациентов в Лондоне ставят их частно. Многие частные клиники, включая нашу, предлагают 0% рассрочку до 12 месяцев на выбранные виды лечения при условии одобрения. Если имплант вам не подходит, врач объяснит альтернативы — мост или съёмный протез.',
            ro: 'Implanturile sunt rareori disponibile prin NHS și de obicei doar în situații clinice specifice, așa că majoritatea pacienților din Londra le fac privat. Multe clinici private, inclusiv a noastră, oferă finanțare 0% până la 12 luni pentru anumite tratamente, sub rezerva aprobării. Dacă implantul nu este potrivit, medicul vă poate explica alternative precum puntea sau proteza.',
          },
        },
      ],
    },
    {
      heading: {
        en: 'Dental implants at Dentico Clinic in Angel',
        ua: 'Імплантація в Dentico Clinic в Angel',
        ru: 'Имплантация в Dentico Clinic в Angel',
        ro: 'Implanturi dentare la Dentico Clinic în Angel',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'At Dentico Clinic we start with an assessment and 3D planning, then give you a written plan with itemised costs before any treatment begins. You can read more about the procedure on our [dental implants page](/implanty-london), check current fees on the [prices page](/prices), or [contact the clinic](/contact) with any questions. Consultations are available in English, Ukrainian, Russian and Romanian.',
            ua: 'У Dentico Clinic ми починаємо з огляду і 3D-планування, а потім надаємо письмовий план з розписаною вартістю ще до початку лікування. Детальніше про процедуру — на сторінці [імплантації зубів](/implanty-london), актуальні ціни — на [сторінці цін](/prices), а з будь-якими питаннями можна [зв’язатися з клінікою](/contact). Консультації доступні англійською, українською, російською та румунською.',
            ru: 'В Dentico Clinic мы начинаем с осмотра и 3D-планирования, а затем предоставляем письменный план с расписанной стоимостью ещё до начала лечения. Подробнее о процедуре — на странице [имплантации зубов](/implanty-london), актуальные цены — на [странице цен](/prices), а с любыми вопросами можно [связаться с клиникой](/contact). Консультации доступны на английском, украинском, русском и румынском.',
            ro: 'La Dentico Clinic începem cu o evaluare și planificare 3D, apoi vă oferim un plan scris cu costuri detaliate înainte de orice tratament. Aflați mai multe pe [pagina de implanturi dentare](/implanty-london), verificați tarifele pe [pagina de prețuri](/prices) sau [contactați clinica](/contact) pentru întrebări. Consultațiile sunt disponibile în engleză, ucraineană, rusă și română.',
          },
        },
      ],
    },
  ],
  faqs: [
    {
      q: { en: 'Are dental implants available on the NHS?', ua: 'Чи можна поставити імпланти за NHS?', ru: 'Можно ли поставить импланты по NHS?', ro: 'Sunt implanturile disponibile prin NHS?' },
      a: {
        en: 'Only in limited clinical cases, for example after trauma or certain medical conditions. Most patients in the UK have implants privately.',
        ua: 'Лише в обмежених клінічних випадках, наприклад після травми або за певних медичних станів. Більшість пацієнтів у Великій Британії ставлять імпланти приватно.',
        ru: 'Только в ограниченных клинических случаях, например после травмы или при определённых медицинских состояниях. Большинство пациентов в Великобритании ставят импланты частно.',
        ro: 'Doar în cazuri clinice limitate, de exemplu după traumatisme sau anumite afecțiuni medicale. Majoritatea pacienților din Marea Britanie își fac implanturile privat.',
      },
    },
    {
      q: { en: 'What should a full implant price include?', ua: 'Що має входити в повну вартість імпланта?', ru: 'Что должно входить в полную стоимость импланта?', ro: 'Ce ar trebui să includă prețul complet al unui implant?' },
      a: {
        en: 'Usually the consultation, imaging, implant placement, abutment and final crown. Ask whether bone grafting, temporary teeth and reviews are included.',
        ua: 'Зазвичай консультацію, діагностику, встановлення імпланта, абатмент і постійну коронку. Уточніть, чи входять кісткова пластика, тимчасові зуби та контрольні огляди.',
        ru: 'Обычно консультацию, диагностику, установку импланта, абатмент и постоянную коронку. Уточните, входят ли костная пластика, временные зубы и контрольные осмотры.',
        ro: 'De obicei consultația, imagistica, inserarea implantului, bontul și coroana finală. Întrebați dacă sunt incluse adiția osoasă, dinții provizorii și controalele.',
      },
    },
    {
      q: { en: 'How long does implant treatment take?', ua: 'Скільки триває лікування імплантами?', ru: 'Сколько длится лечение имплантами?', ro: 'Cât durează tratamentul cu implant?' },
      a: {
        en: 'Typically 3–6 months from placement to the final crown, depending on healing and whether bone grafting is needed.',
        ua: 'Зазвичай 3–6 місяців від встановлення до постійної коронки — залежно від загоєння і потреби в кістковій пластиці.',
        ru: 'Обычно 3–6 месяцев от установки до постоянной коронки — в зависимости от заживления и необходимости костной пластики.',
        ro: 'De obicei 3–6 luni de la inserare până la coroana finală, în funcție de vindecare și de necesitatea adiției osoase.',
      },
    },
    {
      q: { en: 'Can I pay for implants in instalments?', ua: 'Чи можна оплатити імпланти частинами?', ru: 'Можно ли оплатить импланты частями?', ro: 'Pot plăti implanturile în rate?' },
      a: {
        en: 'Dentico Clinic offers 0% finance for up to 12 months on selected treatments, subject to approval. Terms and conditions apply.',
        ua: 'Dentico Clinic пропонує 0% розстрочку до 12 місяців на вибрані види лікування за умови схвалення. Застосовуються умови.',
        ru: 'Dentico Clinic предлагает 0% рассрочку до 12 месяцев на выбранные виды лечения при условии одобрения. Применяются условия.',
        ro: 'Dentico Clinic oferă finanțare 0% până la 12 luni pentru anumite tratamente, sub rezerva aprobării. Se aplică termeni și condiții.',
      },
    },
    {
      q: { en: 'Is everyone suitable for dental implants?', ua: 'Чи всім підходять імпланти?', ru: 'Всем ли подходят импланты?', ro: 'Este toată lumea potrivită pentru implanturi?' },
      a: {
        en: 'Not always. Gum health, bone volume, smoking and some medical conditions affect suitability. A clinical examination and scan are needed to decide.',
        ua: 'Не завжди. На придатність впливають стан ясен, об’єм кістки, куріння та деякі захворювання. Для рішення потрібні клінічний огляд і знімок.',
        ru: 'Не всегда. На пригодность влияют состояние дёсен, объём кости, курение и некоторые заболевания. Для решения нужны клинический осмотр и снимок.',
        ro: 'Nu întotdeauna. Sănătatea gingiilor, volumul osos, fumatul și unele afecțiuni influențează eligibilitatea. Decizia necesită examinare clinică și scanare.',
      },
    },
  ],
  readAlso: [
    {
      to: '/implanty-london',
      label: { en: 'Dental Implants in London', ua: 'Імплантація зубів у Лондоні', ru: 'Имплантация зубов в Лондоне', ro: 'Implanturi dentare în Londra' },
    },
    {
      to: '/services/crowns',
      label: { en: 'Dental Crowns', ua: 'Зубні коронки', ru: 'Зубные коронки', ro: 'Coroane dentare' },
    },
  ],
}
